/**
 * UserCard Component - Displays individual user information in a collapsible card
 */
import React, { useState, memo } from 'react';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { CheckCircle, XCircle, Building, ChevronDown, ChevronRight } from 'lucide-react';
import { colors } from './constants';

const UserCard = memo(({ user, status, onUserClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <XCircle className="h-4 w-4 text-orange-600" />;
    }
  };
  
  const getBgColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-50';
      case 'expired':
        return 'bg-red-50';
      default:
        return 'bg-orange-50';
    }
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`rounded-lg border ${getBgColor()}`} 
           style={{ borderColor: colors.border }}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/50 transition-colors">
            {getStatusIcon()}
            <div className="flex-1 min-w-0">
              <p 
                className="font-medium truncate cursor-pointer hover:underline" 
                style={{ color: colors.dark }}
                onClick={(e) => {
                  e.stopPropagation();
                  onUserClick && onUserClick(user);
                }}
              >
                {user.user_name || 'Unbekannt'}
              </p>
              <div className="flex items-center gap-2 text-xs" style={{ color: colors.gray }}>
                {user.team && (
                  <span className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    {user.team}
                  </span>
                )}
              </div>
            </div>
            {user.course_name && (
              <Badge variant="outline" className="text-xs">
                {user.course_name}
              </Badge>
            )}
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 text-xs border-t space-y-1" style={{ borderColor: colors.border }}>
            {user.status && <p><strong>Status:</strong> {user.status}</p>}
            {user.completion_date && <p><strong>Abgeschlossen:</strong> {user.completion_date}</p>}
            {user.invited_date && <p><strong>Eingeladen:</strong> {user.invited_date}</p>}
            {user.expiry_date && <p><strong>Ablauf:</strong> {user.expiry_date}</p>}
            {user.progress && <p><strong>Fortschritt:</strong> {user.progress}</p>}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
});

UserCard.displayName = 'UserCard';

export default UserCard;
